import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Calendar } from '../ui/calendar';
import {
  Briefcase, Users, Calendar as CalendarIcon, UserCheck, RefreshCw,
  Search, UserX, Clock, Award, UserPlus, TrendingUp, ExternalLink,
  Eye, MessageSquare, ChevronDown, Loader2, MapPin, Phone, Mail, Tag
} from 'lucide-react';
import { useRecruitment } from '../../context/RecruitmentContext';
import { format, startOfDay, endOfDay, subDays, startOfWeek, startOfMonth } from 'date-fns';

const TIME_PERIODS = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'tomorrow', label: 'Tomorrow' },
  { value: 'this_week', label: 'This Week' },
  { value: 'last_7_days', label: 'Last 7 Days' },
  { value: 'current_month', label: 'Current Month' },
  { value: 'last_30_days', label: 'Last 30 Days' },
  { value: 'custom', label: 'Custom Time Period' },
];

function getDateRange(period) {
  const now = new Date();
  const fmt = (d) => format(d, 'yyyy-MM-dd');
  switch (period) {
    case 'today':
      return { from: fmt(now), to: fmt(now) };
    case 'yesterday': {
      const y = subDays(now, 1);
      return { from: fmt(y), to: fmt(y) };
    }
    case 'tomorrow': {
      const t = new Date(now);
      t.setDate(t.getDate() + 1);
      return { from: fmt(t), to: fmt(t) };
    }
    case 'this_week':
      return { from: fmt(startOfWeek(now, { weekStartsOn: 1 })), to: fmt(now) };
    case 'last_7_days':
      return { from: fmt(subDays(now, 6)), to: fmt(now) };
    case 'current_month':
      return { from: fmt(startOfMonth(now)), to: fmt(now) };
    case 'last_30_days':
      return { from: fmt(subDays(now, 29)), to: fmt(now) };
    default:
      return { from: fmt(now), to: fmt(now) };
  }
}

// Default for interviews scheduled: today + next 3 days
function getScheduledDefault() {
  const now = new Date();
  const future = new Date(now);
  future.setDate(future.getDate() + 3);
  return { from: format(now, 'yyyy-MM-dd'), to: format(future, 'yyyy-MM-dd') };
}

const STAGE_COLORS = {
  'Screening': 'bg-blue-100 text-blue-700',
  'Interview': 'bg-purple-100 text-purple-700',
  'Offer': 'bg-amber-100 text-amber-700',
  'Hired': 'bg-green-100 text-green-700',
  'Rejected': 'bg-red-100 text-red-700',
  'On Hold': 'bg-slate-100 text-slate-600',
  'New': 'bg-sky-100 text-sky-700',
};

const RecruitmentDashboard = ({ onRefresh, onSearchCandidates, onViewCandidate }) => {
  const { getEnhancedDashboardStats, getCandidate } = useRecruitment();
  const [searchQuery, setSearchQuery] = useState('');
  const [timePeriod, setTimePeriod] = useState('last_7_days');
  const [customFrom, setCustomFrom] = useState(null);
  const [customTo, setCustomTo] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Popups
  const [candidatePopup, setCandidatePopup] = useState(null);
  const [candidatePopupLoading, setCandidatePopupLoading] = useState(false);
  const [feedbackPopup, setFeedbackPopup] = useState(null);

  const loadStats = useCallback(async () => {
    setLoading(true);
    let dateRange;
    if (timePeriod === 'custom' && customFrom && customTo) {
      dateRange = { from: format(customFrom, 'yyyy-MM-dd'), to: format(customTo, 'yyyy-MM-dd') };
    } else if (timePeriod !== 'custom') {
      dateRange = getDateRange(timePeriod);
    } else {
      dateRange = getDateRange('last_7_days');
    }
    const data = await getEnhancedDashboardStats(dateRange.from, dateRange.to);
    setStats(data);
    setLoading(false);
  }, [timePeriod, customFrom, customTo, getEnhancedDashboardStats]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const handleSearch = () => {
    if (searchQuery.trim() && onSearchCandidates) {
      onSearchCandidates(searchQuery.trim());
    }
  };

  const openCandidatePopup = async (candidateId) => {
    setCandidatePopupLoading(true);
    setCandidatePopup({ id: candidateId });
    const data = await getCandidate(candidateId);
    setCandidatePopup(data);
    setCandidatePopupLoading(false);
  };

  const openFeedbackPopup = (interview) => {
    setFeedbackPopup(interview);
  };

  const openCandidateNewWindow = (candidateId) => {
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set('tab', 'candidates');
    currentUrl.searchParams.set('candidate_id', candidateId);
    window.open(currentUrl.toString(), '_blank');
  };

  const kpiCards = stats ? [
    { label: 'Total Candidates', value: stats.total_candidates, icon: Users, color: 'text-slate-700', bg: 'bg-slate-50', unfiltered: true },
    { label: 'Active Jobs', value: stats.active_jobs, icon: Briefcase, color: 'text-indigo-700', bg: 'bg-indigo-50', unfiltered: true },
    { label: 'Candidates Added', value: stats.candidates_added, icon: UserPlus, color: 'text-blue-700', bg: 'bg-blue-50' },
    { label: 'Interviews Scheduled', value: stats.interviews_scheduled, icon: CalendarIcon, color: 'text-purple-700', bg: 'bg-purple-50' },
    { label: 'Candidates Rejected', value: stats.candidates_rejected, icon: UserX, color: 'text-red-700', bg: 'bg-red-50' },
    { label: 'Candidates Offered', value: stats.candidates_offered, icon: Award, color: 'text-amber-700', bg: 'bg-amber-50' },
    { label: 'Candidates On Hold', value: stats.candidates_on_hold, icon: Clock, color: 'text-slate-600', bg: 'bg-slate-100' },
    { label: 'Candidates Hired', value: stats.candidates_hired, icon: UserCheck, color: 'text-green-700', bg: 'bg-green-50' },
  ] : [];

  const periodLabel = TIME_PERIODS.find(t => t.value === timePeriod)?.label || 'Last 7 Days';

  return (
    <div className="space-y-6" data-testid="recruitment-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Dashboard</h2>
          <p className="text-slate-500">Overview of recruitment activities</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={loadStats} variant="outline" size="sm" disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Universal Search */}
      <Card>
        <CardContent className="pt-6 pb-4">
          <div className="flex gap-3 items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Search candidates by name, location, skills, or tags..."
                className="pl-10"
                data-testid="dashboard-universal-search"
              />
            </div>
            <Button
              onClick={handleSearch}
              className="bg-purple-600 hover:bg-purple-700 gap-2"
              disabled={!searchQuery.trim()}
              data-testid="dashboard-search-btn"
            >
              <Search className="w-4 h-4" />
              Search
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Time Period Filter */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Time Period</Label>
          <Select value={timePeriod} onValueChange={setTimePeriod}>
            <SelectTrigger className="w-[200px]" data-testid="time-period-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TIME_PERIODS.map(tp => (
                <SelectItem key={tp.value} value={tp.value}>{tp.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {timePeriod === 'custom' && (
          <>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">From</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-[160px] justify-start text-left font-normal" data-testid="custom-from-date">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {customFrom ? format(customFrom, 'dd MMM yyyy') : 'From date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={customFrom} onSelect={setCustomFrom} />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">To</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-[160px] justify-start text-left font-normal" data-testid="custom-to-date">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {customTo ? format(customTo, 'dd MMM yyyy') : 'To date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={customTo} onSelect={setCustomTo} />
                </PopoverContent>
              </Popover>
            </div>
            <Button
              size="sm"
              onClick={loadStats}
              disabled={!customFrom || !customTo}
              className="bg-purple-600 hover:bg-purple-700"
            >
              Apply
            </Button>
          </>
        )}
      </div>

      {/* KPI Cards */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        </div>
      ) : stats ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {kpiCards.map(kpi => {
              const Icon = kpi.icon;
              return (
                <Card key={kpi.label} className={kpi.bg}>
                  <CardContent className="pt-5 pb-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">{kpi.label}</p>
                        <p className={`text-2xl font-bold ${kpi.color}`}>{kpi.value}</p>
                        {kpi.unfiltered && (
                          <span className="text-[10px] text-muted-foreground">All time</span>
                        )}
                      </div>
                      <Icon className={`w-8 h-8 ${kpi.color} opacity-40`} />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Job-wise Candidates Interviewed */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <UserCheck className="w-5 h-5 text-purple-600" />
                Candidates Interviewed
                <Badge variant="outline" className="ml-2 font-normal">{periodLabel}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {stats.interviewed_by_job?.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">No interviews completed in this period</p>
              ) : (
                <div className="space-y-6">
                  {stats.interviewed_by_job?.map(group => (
                    <div key={group.job_id}>
                      <div className="flex items-center gap-2 mb-2">
                        <Briefcase className="w-4 h-4 text-indigo-500" />
                        <h4 className="font-semibold text-sm text-slate-800">{group.job_title}</h4>
                        <Badge variant="secondary" className="text-xs">{group.candidates.length}</Badge>
                      </div>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Candidate</TableHead>
                            <TableHead>Current Stage</TableHead>
                            <TableHead>Round</TableHead>
                            <TableHead>Interview Date</TableHead>
                            <TableHead>Recommendation</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {group.candidates.map(c => (
                            <TableRow key={c.interview_id || c.id}>
                              <TableCell>
                                <button
                                  className="text-purple-600 hover:underline font-medium text-left"
                                  onClick={() => openCandidatePopup(c.id)}
                                  data-testid={`interviewed-candidate-${c.id}`}
                                >
                                  {c.name}
                                </button>
                              </TableCell>
                              <TableCell>
                                <Badge className={`${STAGE_COLORS[c.current_stage] || 'bg-slate-100 text-slate-600'} text-xs`}>
                                  {c.current_stage || 'N/A'}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-sm">{c.round_name || '-'}</TableCell>
                              <TableCell className="text-sm">{c.interview_date ? format(new Date(c.interview_date), 'dd MMM yyyy') : '-'}</TableCell>
                              <TableCell>
                                {c.recommendation ? (
                                  <Badge variant={c.recommendation.includes('Hire') ? 'default' : 'destructive'} className="text-xs">
                                    {c.recommendation}
                                  </Badge>
                                ) : <span className="text-muted-foreground text-xs">Pending</span>}
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex justify-end gap-1">
                                  {c.feedback && (
                                    <Button
                                      variant="ghost" size="sm"
                                      onClick={() => openFeedbackPopup(c)}
                                      className="h-7 px-2 text-xs gap-1"
                                      data-testid={`feedback-btn-${c.interview_id || c.id}`}
                                    >
                                      <MessageSquare className="w-3 h-3" />
                                      Feedback
                                    </Button>
                                  )}
                                  <Button
                                    variant="ghost" size="sm"
                                    onClick={() => openCandidateNewWindow(c.id)}
                                    className="h-7 px-2 text-xs gap-1"
                                  >
                                    <ExternalLink className="w-3 h-3" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Job-wise Interviews Scheduled */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <CalendarIcon className="w-5 h-5 text-purple-600" />
                Interviews Scheduled
                <Badge variant="outline" className="ml-2 font-normal">{periodLabel}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {stats.scheduled_by_job?.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">No interviews scheduled in this period</p>
              ) : (
                <div className="space-y-6">
                  {stats.scheduled_by_job?.map(group => (
                    <div key={group.job_id}>
                      <div className="flex items-center gap-2 mb-2">
                        <Briefcase className="w-4 h-4 text-indigo-500" />
                        <h4 className="font-semibold text-sm text-slate-800">{group.job_title}</h4>
                        <Badge variant="secondary" className="text-xs">{group.interviews.length}</Badge>
                      </div>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Candidate</TableHead>
                            <TableHead>Round</TableHead>
                            <TableHead>Scheduled At</TableHead>
                            <TableHead>Interviewer</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {group.interviews.map(iv => (
                            <TableRow key={iv.id}>
                              <TableCell>
                                <button
                                  className="text-purple-600 hover:underline font-medium text-left"
                                  onClick={() => openCandidatePopup(iv.candidate_id)}
                                  data-testid={`scheduled-candidate-${iv.candidate_id}`}
                                >
                                  {iv.candidate_name}
                                </button>
                              </TableCell>
                              <TableCell className="text-sm">{iv.round_name} (R{iv.round_number})</TableCell>
                              <TableCell className="text-sm">
                                {iv.scheduled_at ? format(new Date(iv.scheduled_at), 'dd MMM yyyy, hh:mm a') : '-'}
                              </TableCell>
                              <TableCell className="text-sm">{iv.interviewer_name || '-'}</TableCell>
                              <TableCell>
                                <Badge variant="outline" className="text-xs">{iv.status}</Badge>
                              </TableCell>
                              <TableCell className="text-right">
                                <Button
                                  variant="ghost" size="sm"
                                  onClick={() => openCandidateNewWindow(iv.candidate_id)}
                                  className="h-7 px-2 text-xs gap-1"
                                >
                                  <ExternalLink className="w-3 h-3" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      ) : (
        <p className="text-center text-muted-foreground py-8">Failed to load dashboard data</p>
      )}

      {/* Candidate Detail Popup */}
      <Dialog open={!!candidatePopup} onOpenChange={() => setCandidatePopup(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Candidate Details</DialogTitle>
          </DialogHeader>
          {candidatePopupLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
            </div>
          ) : candidatePopup ? (
            <div className="space-y-4" data-testid="candidate-popup">
              <div>
                <h3 className="text-lg font-semibold">{candidatePopup.name}</h3>
                {candidatePopup.current_stage && (
                  <Badge className={`${STAGE_COLORS[candidatePopup.current_stage] || 'bg-slate-100 text-slate-600'} mt-1`}>
                    {candidatePopup.current_stage}
                  </Badge>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-slate-400" />
                  <span>{candidatePopup.email || 'N/A'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-slate-400" />
                  <span>{candidatePopup.phone || 'N/A'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-slate-400" />
                  <span>{candidatePopup.location || 'N/A'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Briefcase className="w-4 h-4 text-slate-400" />
                  <span>{candidatePopup.current_company || 'N/A'}</span>
                </div>
              </div>
              {candidatePopup.skills?.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {candidatePopup.skills.map(s => (
                    <Badge key={s} variant="secondary" className="text-xs">{s}</Badge>
                  ))}
                </div>
              )}
              {candidatePopup.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {candidatePopup.tags.map(t => (
                    <Badge key={t} variant="outline" className="text-xs"><Tag className="w-3 h-3 mr-1" />{t}</Badge>
                  ))}
                </div>
              )}
              <div className="flex gap-2 pt-2 border-t">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setCandidatePopup(null);
                    if (onViewCandidate) onViewCandidate(candidatePopup.id);
                  }}
                  className="gap-1"
                  data-testid="popup-view-candidate"
                >
                  <Eye className="w-3 h-3" />
                  Open Full Details
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => openCandidateNewWindow(candidatePopup.id)}
                  className="gap-1"
                >
                  <ExternalLink className="w-3 h-3" />
                  Open in New Window
                </Button>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Feedback Popup */}
      <Dialog open={!!feedbackPopup} onOpenChange={() => setFeedbackPopup(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Interview Feedback</DialogTitle>
          </DialogHeader>
          {feedbackPopup && (
            <div className="space-y-3" data-testid="feedback-popup">
              <div>
                <p className="text-sm text-muted-foreground">Candidate</p>
                <p className="font-medium">{feedbackPopup.name}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Round</p>
                <p>{feedbackPopup.round_name || 'N/A'}</p>
              </div>
              {feedbackPopup.recommendation && (
                <div>
                  <p className="text-sm text-muted-foreground">Recommendation</p>
                  <Badge variant={feedbackPopup.recommendation.includes('Hire') ? 'default' : 'destructive'}>
                    {feedbackPopup.recommendation}
                  </Badge>
                </div>
              )}
              <div>
                <p className="text-sm text-muted-foreground mb-1">Feedback</p>
                <div className="bg-slate-50 rounded-lg p-3 text-sm whitespace-pre-wrap">
                  {feedbackPopup.feedback || 'No feedback provided.'}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RecruitmentDashboard;
